

import { useEffect, useState } from 'react';
import { Plus, Loader2, Pencil, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { authFetch } from '@/lib/auth-client';
import {
  buildManualPropDescription,
  expandManualPropAcrossGames,
  supportsFanOut,
  type MatchScope,
} from '@/services/events/main-event/manual-prop.template';
import { MANUAL_PROP_PRESETS } from '@/services/events/main-event/manual-prop.presets';

export type ManualPropType =
  | 'MANUAL_TEAM_THRESHOLD'
  | 'MANUAL_MATCH_THRESHOLD'
  | 'MANUAL_PLAYER_BOOLEAN'
  | 'MANUAL_GENERIC';

export type ManualPickMode = 'BUY_ONLY' | 'YES_NO';

export interface ManualModalGame {
  id: string;
  homeTeam: string;
  awayTeam: string;
}

export interface ManualPropForEdit {
  id: string;
  description: string;
  costCoins: number;
  bonusMultiplier: number;
  propType: ManualPropType;
  pickMode: ManualPickMode;
  subjectTeam: string | null;
  subjectPlayer: string | null;
  threshold: number | null;
  statCategory: string | null;
  gameId: string | null;
}

export interface ManualPredictionModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  mainEventId: string;
  undercardId: string;
  games: ManualModalGame[];
  pointsPerCorrectPick: number;
  existingProp?: ManualPropForEdit | null;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

const MATCH_TEMPLATES: { value: ManualPropType; label: string; hint: string }[] = [
  { value: 'MANUAL_TEAM_THRESHOLD', label: 'Team Threshold', hint: 'Single team in a match' },
  { value: 'MANUAL_MATCH_THRESHOLD', label: 'Match Threshold', hint: 'Either team in a match' },
  { value: 'MANUAL_PLAYER_BOOLEAN', label: 'Player Boolean', hint: 'A specific player' },
  { value: 'MANUAL_GENERIC', label: 'Custom', hint: 'Free-form (round or match)' },
];

const STAT_PRESETS = ['GOALS', 'POINTS', 'ASSISTS', 'REBOUNDS', 'SHOTS', 'SAVES', 'STRIKEOUTS', 'TOUCHDOWNS'];

/**
 * Compute the form's customDescription field for edit mode.
 * If the prop's stored description matches what `buildManualPropDescription` would produce
 * from the structured fields, the prop is templated — leave override blank so structural
 * edits regenerate. Otherwise it was a custom override, pre-fill it for editing.
 */
const detectCustomOverride = (
  prop: ManualPropForEdit,
  games: ManualModalGame[]
): string => {
  const game = prop.gameId ? games.find((g) => g.id === prop.gameId) : null;
  const templated = buildManualPropDescription({
    propType: prop.propType,
    subjectTeam: prop.subjectTeam,
    subjectPlayer: prop.subjectPlayer,
    threshold: prop.threshold,
    statCategory: prop.statCategory,
    homeTeam: game?.homeTeam ?? null,
    awayTeam: game?.awayTeam ?? null,
  });
  if (templated && templated === prop.description) return '';
  return prop.description ?? '';
};

const initialForm = (existing?: ManualPropForEdit | null, games: ManualModalGame[] = []) => ({
  propType: (existing?.propType ?? 'MANUAL_MATCH_THRESHOLD') as ManualPropType,
  // Manual predictions force YES_NO. BUY_ONLY is preserved on the schema for legacy
  // odds-based props, but is intentionally not surfaced in the manual modal.
  pickMode: 'YES_NO' as ManualPickMode,
  costCoins: existing?.costCoins ?? 100,
  bonusMultiplier: existing?.bonusMultiplier ?? 1.5,
  subjectTeam: existing?.subjectTeam ?? '',
  subjectPlayer: existing?.subjectPlayer ?? '',
  threshold: existing?.threshold == null ? '' : String(existing.threshold),
  statCategory: existing?.statCategory ?? 'GOALS',
  customDescription: existing ? detectCustomOverride(existing, games) : '',
  gameId: existing?.gameId ?? '',
  applyToAllGames: false,
  matchScope: 'EITHER_TEAM' as MatchScope,
});

export function ManualPredictionModal({
  open,
  mode,
  mainEventId,
  undercardId,
  games,
  pointsPerCorrectPick,
  existingProp,
  onClose,
  onSuccess,
  onError,
}: ManualPredictionModalProps) {
  const [form, setForm] = useState(() => initialForm(existingProp, games));
  const [submitting, setSubmitting] = useState(false);

  // Reset form when opening with new context
  useEffect(() => {
    if (open) {
      setForm(initialForm(existingProp, games));
    }
  }, [open, existingProp, games]);

  if (!open) return null;

  // When admin changes the template, clear orphaned fields that don't apply.
  const setPropType = (next: ManualPropType) => {
    setForm((f) => {
      const wantsTeam = next === 'MANUAL_TEAM_THRESHOLD';
      const wantsPlayer = next === 'MANUAL_PLAYER_BOOLEAN';
      const wantsThreshold = next !== 'MANUAL_GENERIC';
      const wantsStat = next !== 'MANUAL_GENERIC';
      // Custom + applyToAllGames doesn't need a gameId; MATCH_THRESHOLD without fan-out requires one.
      // Don't clear gameId on type change — admin may have selected a game intentionally.
      return {
        ...f,
        propType: next,
        subjectTeam: wantsTeam ? f.subjectTeam : '',
        subjectPlayer: wantsPlayer ? f.subjectPlayer : '',
        threshold: wantsThreshold ? f.threshold : '',
        statCategory: wantsStat ? (f.statCategory || 'GOALS') : '',
        // Fan-out only applies to MATCH_THRESHOLD and Custom; clear when switching away.
        applyToAllGames: supportsFanOut(next) ? f.applyToAllGames : false,
      };
    });
  };

  const fanOutEligible = supportsFanOut(form.propType);
  const showGameSelector = !form.applyToAllGames;
  const showThresholdField = form.propType === 'MANUAL_TEAM_THRESHOLD' || form.propType === 'MANUAL_MATCH_THRESHOLD' || form.propType === 'MANUAL_PLAYER_BOOLEAN';
  const showStatField = showThresholdField;
  const showSubjectTeam = form.propType === 'MANUAL_TEAM_THRESHOLD';
  const showSubjectPlayer = form.propType === 'MANUAL_PLAYER_BOOLEAN';
  const customIsRequired = form.propType === 'MANUAL_GENERIC';

  // Live preview: for MATCH_THRESHOLD, use the fan-out helper to render scope-aware
  // preview lines (1 line for EITHER_TEAM/COMBINED_TOTAL, 2 for EACH_TEAM).
  // For other propTypes, fall back to single-line buildManualPropDescription.
  const previewGame = form.gameId ? games.find((g) => g.id === form.gameId) : null;
  const fanOutPreviewGame = form.applyToAllGames && games.length > 0 ? games[0] : null;
  const previewLines: string[] = (() => {
    if (form.propType !== 'MANUAL_MATCH_THRESHOLD') {
      const desc = buildManualPropDescription({
        propType: form.propType,
        subjectTeam: form.subjectTeam,
        subjectPlayer: form.subjectPlayer,
        threshold: form.threshold === '' ? null : Number(form.threshold),
        statCategory: form.statCategory,
        customDescription: form.customDescription,
        homeTeam: previewGame?.homeTeam ?? null,
        awayTeam: previewGame?.awayTeam ?? null,
      });
      return desc ? [desc] : [];
    }
    // MATCH_THRESHOLD: use scope-aware expansion against either the chosen single
    // game or the first fan-out game (for an example).
    const refGame = previewGame ?? fanOutPreviewGame;
    if (!refGame) {
      // No game context yet — show the placeholder template
      const desc = buildManualPropDescription({
        propType: form.propType,
        threshold: form.threshold === '' ? null : Number(form.threshold),
        statCategory: form.statCategory,
        customDescription: form.customDescription,
      });
      return desc ? [desc] : [];
    }
    const drafts = expandManualPropAcrossGames(
      {
        propType: form.propType,
        threshold: form.threshold === '' ? null : Number(form.threshold),
        statCategory: form.statCategory,
        customDescription: form.customDescription,
      },
      [{ id: refGame.id, homeTeam: refGame.homeTeam, awayTeam: refGame.awayTeam }],
      form.matchScope
    );
    return drafts.map((d) => d.description).filter(Boolean);
  })();

  const estimatedPoints =
    Number.isFinite(form.bonusMultiplier) && form.bonusMultiplier > 0
      ? form.bonusMultiplier * pointsPerCorrectPick
      : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    const thresholdNum = form.threshold === '' ? null : Number(form.threshold);
    if (form.threshold !== '' && Number.isNaN(thresholdNum)) {
      onError('Threshold must be a number');
      setSubmitting(false);
      return;
    }

    // Only send fields relevant to the chosen propType so we don't trip the
    // server's "orphan field" cross-validation.
    const wantsTeam = form.propType === 'MANUAL_TEAM_THRESHOLD';
    const wantsPlayer = form.propType === 'MANUAL_PLAYER_BOOLEAN';
    const wantsThreshold =
      form.propType === 'MANUAL_TEAM_THRESHOLD' ||
      form.propType === 'MANUAL_MATCH_THRESHOLD' ||
      form.propType === 'MANUAL_PLAYER_BOOLEAN';
    const wantsStat = wantsThreshold;

    const payload: Record<string, unknown> = {
      propType: form.propType,
      pickMode: form.pickMode,
      costCoins: form.costCoins,
      bonusMultiplier: form.bonusMultiplier,
      subjectTeam: wantsTeam ? form.subjectTeam.trim() || null : null,
      subjectPlayer: wantsPlayer ? form.subjectPlayer.trim() || null : null,
      threshold: wantsThreshold ? thresholdNum : null,
      statCategory: wantsStat ? form.statCategory.trim() || null : null,
      customDescription: form.customDescription.trim() || null,
    };

    if (mode === 'create') {
      payload.applyToAllGames = !!form.applyToAllGames;
      payload.gameId = form.applyToAllGames ? null : form.gameId || null;
      // matchScope is meaningful any time MATCH_THRESHOLD is selected — single-game
      // or fan-out. Server uses it to decide whether to emit 1 or 2 props per game.
      if (form.propType === 'MANUAL_MATCH_THRESHOLD') {
        payload.matchScope = form.matchScope;
      }
    } else {
      payload.gameId = form.gameId || null;
    }

    try {
      const url =
        mode === 'create'
          ? `/api/admin/main-events/${mainEventId}/undercards/${undercardId}/props/manual`
          : `/api/admin/main-events/${mainEventId}/undercards/${undercardId}/props/${existingProp!.id}/manual`;
      const method = mode === 'create' ? 'POST' : 'PATCH';
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        const created = data?.created;
        const successMsg =
          mode === 'create'
            ? created && created > 1
              ? `Created ${created} predictions across all games`
              : 'Manual prediction added'
            : 'Manual prediction updated';
        onSuccess(successMsg);
        onClose();
      } else {
        const data = await res.json().catch(() => ({}));
        onError(data?.error || `Failed to ${mode === 'create' ? 'add' : 'update'} prediction`);
      }
    } catch {
      onError(`Failed to ${mode === 'create' ? 'add' : 'update'} prediction`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4 pt-12">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-2xl bg-zinc-900 rounded-xl border border-amber-500/30 shadow-2xl"
      >
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {mode === 'edit' ? <Pencil className="h-4 w-4 text-amber-400" /> : <Plus className="h-4 w-4 text-amber-400" />}
            <h3 className="text-base font-bold text-amber-300">
              {mode === 'edit' ? 'Edit Manual Prediction' : 'Add Manual Prediction'}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-300 text-xl leading-none">
            ×
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Template selector (create only — propType is locked in edit) */}
          {mode === 'create' && (
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Template *</label>
              <div className="grid grid-cols-4 gap-2">
                {MATCH_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.value}
                    type="button"
                    onClick={() => setPropType(tpl.value)}
                    className={cn(
                      'px-2 py-2 rounded-lg border text-left transition-colors',
                      form.propType === tpl.value
                        ? 'bg-amber-500/10 border-amber-500/50'
                        : 'bg-zinc-950 border-zinc-700 hover:border-zinc-600'
                    )}
                  >
                    <div className="text-[11px] font-bold text-white truncate">{tpl.label}</div>
                    <div className="text-[10px] text-zinc-400 mt-0.5 leading-tight">{tpl.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Match scope selector — for MATCH_THRESHOLD; affects single-game AND fan-out */}
          {mode === 'create' && form.propType === 'MANUAL_MATCH_THRESHOLD' && (
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Match scope *</label>
              <div className="grid grid-cols-3 gap-2">
                {([
                  {
                    v: 'EITHER_TEAM' as MatchScope,
                    l: 'Either team',
                    h: '"X or Y record N+ stat?"',
                    perMatch: 1,
                  },
                  {
                    v: 'EACH_TEAM' as MatchScope,
                    l: 'Each team separately',
                    h: 'One prediction per team',
                    perMatch: 2,
                  },
                  {
                    v: 'COMBINED_TOTAL' as MatchScope,
                    l: 'Combined match total',
                    h: '"N+ stat in this match?"',
                    perMatch: 1,
                  },
                ]).map((opt) => {
                  const matchCount = form.applyToAllGames ? games.length : 1;
                  const totalPredictions = matchCount * opt.perMatch;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, matchScope: opt.v }))}
                      className={cn(
                        'px-2 py-2 rounded-lg border text-left transition-colors',
                        form.matchScope === opt.v
                          ? 'bg-amber-500/10 border-amber-500/50'
                          : 'bg-zinc-950 border-zinc-700 hover:border-zinc-600'
                      )}
                    >
                      <div className="text-[11px] font-bold text-white truncate">{opt.l}</div>
                      <div className="text-[10px] text-zinc-400 mt-0.5 leading-tight">{opt.h}</div>
                      <div className="text-[10px] text-amber-300/80 mt-0.5">
                        → {totalPredictions} prediction{totalPredictions === 1 ? '' : 's'}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Game selector (hidden when fan-out is on) */}
          {showGameSelector && (
            <div>
              <label className="block text-xs text-zinc-400 mb-1">
                Game {form.propType === 'MANUAL_MATCH_THRESHOLD' ? '*' : '(leave blank for round-wide)'}
              </label>
              <select
                value={form.gameId}
                onChange={(e) => setForm((f) => ({ ...f, gameId: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              >
                <option value="">— None (applies to whole round) —</option>
                {games.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.awayTeam} @ {g.homeTeam}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Fan-out checkbox (only for MATCH_THRESHOLD + Custom) */}
          {mode === 'create' && fanOutEligible && (
            <label className="flex items-start gap-2 p-3 rounded-lg bg-zinc-950 border border-zinc-700 cursor-pointer hover:border-zinc-600">
              <input
                type="checkbox"
                checked={form.applyToAllGames}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    applyToAllGames: e.target.checked,
                    gameId: e.target.checked ? '' : f.gameId,
                  }))
                }
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="text-xs font-bold text-white">Apply to all {games.length} games in this round</div>
                <div className="text-[10px] text-zinc-400 mt-0.5">
                  Multiplies the prediction count above across every match.
                </div>
              </div>
            </label>
          )}

          {/* Quick presets — Custom + applyToAllGames flow */}
          {mode === 'create' && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Sparkles className="h-3 w-3 text-amber-400" />
                <label className="block text-xs text-zinc-400">Quick presets</label>
                <span className="text-[10px] text-zinc-600">(prefills Custom + Apply-to-all-games with base-rate-tuned multiplier)</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {MANUAL_PROP_PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        propType: 'MANUAL_GENERIC',
                        applyToAllGames: true,
                        gameId: '',
                        subjectTeam: '',
                        subjectPlayer: '',
                        threshold: '',
                        statCategory: '',
                        customDescription: preset.description,
                        costCoins: preset.suggestedCostCoins,
                        bonusMultiplier: preset.suggestedMultiplier,
                      }))
                    }
                    className="px-2 py-2 rounded-lg border border-zinc-700 hover:border-amber-500/50 bg-zinc-950 text-left transition-colors"
                  >
                    <div className="text-[11px] font-bold text-white truncate">{preset.label}</div>
                    <div className="text-[10px] text-zinc-400 mt-0.5 leading-tight">
                      ~{Math.round(preset.baseRate * 100)}% base rate · {preset.suggestedMultiplier}× boost
                    </div>
                    <div className="text-[10px] text-zinc-500 leading-tight">{preset.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Subject Team */}
          {showSubjectTeam && (
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Team *</label>
              <input
                type="text"
                value={form.subjectTeam}
                onChange={(e) => setForm((f) => ({ ...f, subjectTeam: e.target.value }))}
                placeholder="Argentina"
                className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>
          )}

          {/* Subject Player */}
          {showSubjectPlayer && (
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Player *</label>
              <input
                type="text"
                value={form.subjectPlayer}
                onChange={(e) => setForm((f) => ({ ...f, subjectPlayer: e.target.value }))}
                placeholder="Lionel Messi"
                className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>
          )}

          {/* Threshold + Stat */}
          {showThresholdField && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">
                  {form.propType === 'MANUAL_PLAYER_BOOLEAN' ? 'Threshold (blank = any)' : 'Threshold *'}
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={form.threshold}
                  onChange={(e) => setForm((f) => ({ ...f, threshold: e.target.value }))}
                  placeholder={form.propType === 'MANUAL_PLAYER_BOOLEAN' ? '1' : '2'}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                />
              </div>
              {showStatField && (
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Stat *</label>
                  <input
                    type="text"
                    list="manual-modal-stat-presets"
                    value={form.statCategory}
                    onChange={(e) => setForm((f) => ({ ...f, statCategory: e.target.value.toUpperCase() }))}
                    placeholder="GOALS"
                    className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
                  />
                  <datalist id="manual-modal-stat-presets">
                    {STAT_PRESETS.map((s) => <option key={s} value={s} />)}
                  </datalist>
                </div>
              )}
            </div>
          )}

          {/* Description (override or primary input) */}
          <div>
            <label className="block text-xs text-zinc-400 mb-1">
              {customIsRequired ? 'Description *' : 'Description override (leave blank to use template)'}
            </label>
            <input
              type="text"
              value={form.customDescription}
              onChange={(e) => setForm((f) => ({ ...f, customDescription: e.target.value }))}
              placeholder={customIsRequired ? 'Will the match go to penalties?' : '(auto-generated from template)'}
              className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
            />
          </div>

          {/* Live preview */}
          <div className="p-3 rounded-lg bg-zinc-950/50 border border-zinc-700 min-h-[3rem]">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Preview</div>
            {previewLines.length === 0 ? (
              <div className="text-sm text-zinc-600 italic">Fill in the fields above…</div>
            ) : (
              <div className="space-y-1">
                {previewLines.map((line, i) => (
                  <div key={i} className="text-sm text-amber-300 font-medium leading-tight">
                    {line}
                  </div>
                ))}
              </div>
            )}
            {form.applyToAllGames && form.propType === 'MANUAL_MATCH_THRESHOLD' && games.length > 0 && (
              <div className="mt-2 text-[10px] text-zinc-500">
                Same template applies to all {games.length} matches in this round.
              </div>
            )}
          </div>

          {/* Cost / Multiplier + payout preview */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Cost (coins) *</label>
              <input
                type="number"
                value={form.costCoins}
                onChange={(e) => setForm((f) => ({ ...f, costCoins: Number(e.target.value) }))}
                min={1}
                className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Bonus Multiplier *</label>
              <input
                type="number"
                step="0.1"
                value={form.bonusMultiplier}
                onChange={(e) => setForm((f) => ({ ...f, bonusMultiplier: Number(e.target.value) }))}
                min={0.1}
                className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:border-amber-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Reward on win</label>
              <div className="px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-sm font-mono text-emerald-300">
                +{estimatedPoints.toFixed(2)} points
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                {form.bonusMultiplier}× × {pointsPerCorrectPick} base
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-zinc-800 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-200">
            Cancel
          </button>
          <Button type="submit" size="sm" disabled={submitting}>
            {submitting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
            {mode === 'edit' ? 'Save Changes' : 'Add Prediction'}
          </Button>
        </div>
      </form>
    </div>
  );
}
